import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Building2,
  BookMarked,
  Star,
  TrendingUp,
  Map as MapIcon,
  FolderOpen,
  Download,
} from "lucide-react";
import ScoreGaugePanel from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import type { SubNavItem } from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import ActionPlanIssuesPanel from "@/components/ruby-ai-v2/dashboard/ActionPlanIssuesPanel";
import RubyQuickFixesWidget from "@/components/ruby-ai-v2/dashboard/RubyQuickFixesWidget";
import EmptyState from "@/components/ruby-ai-v2/dashboard/EmptyState";
import DataPanel from "@/components/ruby-ai-v2/dashboard/DataPanel";
import WidgetExportBtn from "@/components/ruby-ai-v2/reports/WidgetExportBtn";
import {
  loadLatestAudits,
  loadLatestAuditByShortcut,
} from "@/components/ruby-ai-v2/services/auditStorageService";
import SiteSwitcher from "@/components/ruby-ai-v2/dashboard/SiteSwitcher";
import ReportSwitcher from "@/components/ruby-ai-v2/dashboard/ReportSwitcher";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import RubyExportDialog from "@/components/ruby-ai-v2/reports/RubyExportDialog";
import { buildFullExportData } from "@/components/ruby-ai-v2/reports/exportDataUtils";

const CARD_ID = "local-intelligence";

const tabs = [
  { id: "overview", label: "Overview", icon: MapPin },
  { id: "gbp", label: "Business Profile", icon: Building2 },
  { id: "citations", label: "Citations", icon: BookMarked },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "rankings", label: "Local Rankings", icon: TrendingUp },
  { id: "roadmap", label: "Roadmap", icon: MapIcon },
] as const;

type TabId = (typeof tabs)[number]["id"];

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

const heroDefaults: Record<TabId, {
  badge: string; title: string; centerLabel: string; subtitle: string; metricLabels: string[];
}> = {
  overview: { badge: "Local Intelligence", title: "Local Intelligence Score", centerLabel: "LOCAL", subtitle: "Import your authority audit to see your local SEO performance.", metricLabels: ["GBP Completeness", "NAP Consistency", "Citations", "Reviews"] },
  gbp: { badge: "Business Profile", title: "GBP Score", centerLabel: "PROFILE", subtitle: "Google Business Profile completeness and optimisation.", metricLabels: ["Completeness", "Rating", "Reviews", "Response Rate"] },
  citations: { badge: "Citations", title: "Citation Score", centerLabel: "CITATIONS", subtitle: "Directory citation reach and NAP consistency.", metricLabels: ["Citations Found", "NAP Score", "Inconsistencies", "Directories"] },
  reviews: { badge: "Reviews", title: "Review Score", centerLabel: "REVIEWS", subtitle: "Review volume, recency, and sentiment across platforms.", metricLabels: ["Total Reviews", "Avg Rating", "Positive %", "Platforms"] },
  rankings: { badge: "Local Rankings", title: "Local Ranking Score", centerLabel: "RANKINGS", subtitle: "Map pack position and local keyword visibility.", metricLabels: ["Map Pack", "Organic", "Keywords", "Competitors"] },
  roadmap: { badge: "Local Strategy", title: "Local Strategy Score", centerLabel: "STRATEGY", subtitle: "Build your local SEO remediation roadmap.", metricLabels: ["Strategy Score", "Actions", "Priority Items", "Timeline"] },
};

const TAB_SUBNAV: Record<TabId, Omit<SubNavItem, "hasData">[]> = {
  overview: [
    { id: "local-overview", label: "Local Overview", shortcutId: "local-overview" },
    { id: "gbp-audit", label: "GBP Audit", shortcutId: "gbp-audit" },
  ],
  gbp: [
    { id: "gbp-completeness", label: "GBP Completeness", shortcutId: "gbp-completeness" },
  ],
  citations: [
    { id: "citation-audit", label: "Citation Audit", shortcutId: "citation-audit" },
    { id: "nap-consistency", label: "NAP Consistency", shortcutId: "nap-consistency" },
  ],
  reviews: [
    { id: "review-signals", label: "Review Signals", shortcutId: "review-signals" },
  ],
  rankings: [
    { id: "local-rankings", label: "Local Rankings", shortcutId: "local-rankings" },
    { id: "map-pack", label: "Map Pack", shortcutId: "map-pack" },
  ],
  roadmap: [
    { id: "local-strategy", label: "Local Strategy", shortcutId: "local-strategy" },
  ],
};

const MetricCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,123,247,0.08)" }}>
    <div className="text-xs mb-1" style={{ color: "#9AA0B3" }}>{label}</div>
    <div className="text-xl font-bold" style={{ color: highlight ? "#FF9A4A" : "#E8E4FF" }}>{value}</div>
  </div>
);

const LocalIntelligenceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const userId = user?.id;
  const auditContextKey = JSON.stringify([userId, activeSite?.domain, selectedReportId]);
  const auditContextRef = useRef(auditContextKey);
  auditContextRef.current = auditContextKey;
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [liveAuditData, setLiveAuditData] = useState<any | null>(null);
  const [activeSubNav, setActiveSubNav] = useState<string>(TAB_SUBNAV.overview[0].id);
  const [availableAudits, setAvailableAudits] = useState<Set<string>>(new Set());
  const [lastAuditMeta, setLastAuditMeta] = useState<{ region: string; domain: string; createdAt: string } | null>(null);
  const [exportDialog, setExportDialog] = useState<{ open: boolean; title: string; tier: "widget" | "chat" | "full"; data: any[]; auditResult?: any }>({ open: false, title: "", tier: "widget", data: [] });

  const openFullReportExport = () => {
    if (!liveAuditData) return;
    setExportDialog({ open: true, title: "Local Intelligence Full Report", tier: "full", data: buildFullExportData(liveAuditData, liveAuditData.requestedAuditType || "local-overview"), auditResult: liveAuditData });
  };

  useEffect(() => {
    const firstItem = TAB_SUBNAV[activeTab]?.[0];
    if (firstItem) setActiveSubNav(firstItem.id);
  }, [activeTab]);

  const loadDashboardAudit = async (domain?: string) => {
    if (!userId || !domain) return;
    setLiveAuditData(null);
    setLastAuditMeta(null);
    const audit = await loadLatestAuditByShortcut({ userId, cardId: CARD_ID, shortcutId: "authority44-import", domain, reportId: selectedReportId });
    if (auditContextRef.current !== auditContextKey) return;
    if (audit) {
      setLiveAuditData(audit.structured_data);
      setLastAuditMeta({ region: audit.region, domain: audit.domain, createdAt: audit.created_at });
    } else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  };

  useEffect(() => {
    if (!userId) return;
    loadLatestAudits({ userId, cardId: CARD_ID, limit: 50 }).then((audits) => {
      const found = new Set<string>();
      for (const a of audits) found.add(a.shortcut_id);
      setAvailableAudits(found);
    }).catch((err) => console.warn("[LocalIntelligence] Failed to load:", err));
    loadDashboardAudit(activeSite?.domain);
  }, [userId]);

  useEffect(() => {
    if (activeSite?.domain && userId) loadDashboardAudit(activeSite.domain);
    else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  }, [activeSite?.domain, userId, selectedReportId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const handleSubNavClick = async (item: SubNavItem) => {
    if (!userId) return;
    setActiveSubNav(item.id);
    const saved = await loadLatestAuditByShortcut({ userId, cardId: CARD_ID, shortcutId: item.shortcutId, domain: activeSite?.domain });
    if (auditContextRef.current !== auditContextKey) return;
    if (saved) {
      setLiveAuditData(saved.structured_data as any);
      setLastAuditMeta({ region: saved.region, domain: saved.domain, createdAt: saved.created_at });
    } else {
      setLiveAuditData(null);
    }
  };

  // ── Derived local data (defensive — shape may vary until A44 data lands) ──
  const local = liveAuditData?.localSEO;
  const gbp = local?.googleBusinessProfile;
  const nap = local?.napConsistency;
  const rankings = local?.localRankings;
  const citations: any[] = liveAuditData?.citations ?? [];
  const reviews = liveAuditData?.reviews;

  const defaults = heroDefaults[activeTab];
  const score = liveAuditData?.cardScore ?? gbp?.completeness ?? 0;
  const subtitle = local ? `Local audit for ${liveAuditData?.domain ?? "your domain"}` : defaults.subtitle;
  const metrics = local ? [
    { label: "GBP Completeness", value: gbp?.completeness != null ? `${gbp.completeness}%` : "—" },
    { label: "NAP Consistency", value: nap?.score != null ? `${nap.score}` : "—" },
    { label: "Citations", value: nap?.citationsFound != null ? String(nap.citationsFound) : String(citations.length) },
    { label: "Reviews", value: reviews?.totalReviews != null ? String(reviews.totalReviews) : "—" },
  ] : defaults.metricLabels.map((l) => ({ label: l, value: "0" }));

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto" style={{ ["--accent" as string]: "#22C55E", maxWidth: "1480px" } as React.CSSProperties}>
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <button onClick={() => navigate("/dashboard")} aria-label="Back to dashboard" className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <SiteSwitcher onSiteChange={(domain) => loadDashboardAudit(domain)} />
          <ReportSwitcher />
          <h1 className="flex items-center gap-2.5 font-heading text-[30px] leading-none text-og01-text">
            <MapPin className="w-[26px] h-[26px]" style={{ color: "var(--accent)" }} />
            Local Intelligence
          </h1>
          <div className="flex-1" />
          <button onClick={openFullReportExport} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border border-white/[0.07] bg-og01-card text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]" style={{ color: liveAuditData ? undefined : "#6B6585" }}>
            <Download className="w-[15px] h-[15px]" />Export Full Report
          </button>
          <button onClick={() => navigate("/resource-vault?card=local-intelligence")} className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border-none text-[13.5px] font-semibold text-white transition-all hover:brightness-110" style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <FolderOpen className="w-[15px] h-[15px]" />Saved Reports
          </button>
        </div>

        {/* Metadata */}
        {lastAuditMeta && (
          <div className="flex items-center gap-5 mb-4 pl-[52px] text-[12.5px] text-og01-text-muted">
            {[
              lastAuditMeta.domain ? <>Domain: <b className="font-medium text-og01-text-secondary">{lastAuditMeta.domain}</b></> : null,
              <>Citations: <b className="font-medium text-og01-text-secondary">{nap?.citationsFound ?? citations.length}</b></>,
              lastAuditMeta.createdAt ? <>Last Run: <b className="font-medium text-og01-text-secondary">{formatTimeAgo(lastAuditMeta.createdAt)}</b></> : null,
            ].filter(Boolean).map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-5 p-1.5 rounded-[14px] overflow-x-auto border border-white/[0.07] bg-og01-card">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-[15px] py-2.5 rounded-[9px] text-[13.5px] font-medium whitespace-nowrap transition-all hover:text-og01-text"
                style={isActive ? { color: "var(--accent)", background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 28%, transparent)" } : { color: "#9AA0B3", border: "1px solid transparent" }}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Score Gauge with Sub-Nav */}
        <div className="mb-6">
          <ScoreGaugePanel
            score={score} scoreChange={0} period="Last 30 Days" subtitle={subtitle} metrics={metrics}
            badge={defaults.badge} title={defaults.title} centerLabel={defaults.centerLabel}
            subNavItems={TAB_SUBNAV[activeTab].map((item) => ({ ...item, hasData: availableAudits.has(item.shortcutId) }))}
            activeSubNav={activeSubNav} onSubNavClick={handleSubNavClick}
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6" style={{ minHeight: "calc(100vh - 200px)" }}>

            <ActionPlanIssuesPanel cardId={CARD_ID} title="Recommended Actions" />

            {local ? (
              <>
                {/* Google Business Profile */}
                <DataPanel title="Google Business Profile" icon={<Building2 className="w-4 h-4" />} actions={<WidgetExportBtn title="Google Business Profile" data={gbp ? [gbp] : []} />}>
                  {gbp ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <MetricCard label="Completeness" value={gbp.completeness != null ? `${gbp.completeness}%` : "—"} highlight />
                      <MetricCard label="Rating" value={gbp.rating != null ? `${gbp.rating}★` : "—"} />
                      <MetricCard label="Reviews" value={gbp.reviewCount != null ? String(gbp.reviewCount) : "—"} />
                      <MetricCard label="Claimed" value={gbp.claimed ? "Yes" : "No"} />
                      <MetricCard label="Verified" value={gbp.verified ? "Yes" : "No"} />
                      <MetricCard label="Response Rate" value={gbp.responseRate != null ? `${gbp.responseRate}%` : "—"} />
                    </div>
                  ) : (
                    <EmptyState title="No GBP data yet" description="Import your authority audit to see Google Business Profile completeness." />
                  )}
                </DataPanel>

                {/* NAP / Citations */}
                <DataPanel title="Citations & NAP Consistency" icon={<BookMarked className="w-4 h-4" />} actions={<WidgetExportBtn title="Citations" data={citations} />}>
                  {citations.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                            {["Directory", "NAP Match", "Last Updated"].map((h) => (
                              <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {citations.slice(0, 15).map((c: any, i: number) => (
                            <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                              <td className="py-2.5 px-2 font-medium" style={{ color: "#8B7BF7" }}>{c.directory ?? "—"}</td>
                              <td className="py-2.5 px-2" style={{ color: c.napMatch ? "#4ADE80" : "#F87171" }}>{c.napMatch ? "Match" : "Mismatch"}</td>
                              <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{c.lastUpdated ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EmptyState title="No citation data yet" description="Import your authority audit to see your directory citations and NAP consistency." />
                  )}
                </DataPanel>

                {/* Local Summary */}
                <DataPanel title="Local SEO Summary" icon={<MapPin className="w-4 h-4" />} actions={<WidgetExportBtn title="Local SEO Summary" data={local ? [{ "GBP Completeness": gbp?.completeness, "NAP Score": nap?.score, "Map Pack": rankings?.mapPackPosition, "Reviews": reviews?.totalReviews }] : []} />}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <MetricCard label="NAP Score" value={nap?.score != null ? String(nap.score) : "—"} highlight />
                    <MetricCard label="Inconsistencies" value={nap?.inconsistencies != null ? String(nap.inconsistencies) : "—"} />
                    <MetricCard label="Map Pack Position" value={rankings?.mapPackPosition != null ? `#${rankings.mapPackPosition}` : "—"} />
                    <MetricCard label="Organic Position" value={rankings?.organicPosition != null ? `#${rankings.organicPosition}` : "—"} />
                    <MetricCard label="Total Reviews" value={reviews?.totalReviews != null ? String(reviews.totalReviews) : "—"} />
                    <MetricCard label="Avg Rating" value={reviews?.averageRating != null ? `${reviews.averageRating}★` : "—"} />
                  </div>
                </DataPanel>
              </>
            ) : (
              <>
                <DataPanel title="Google Business Profile" icon={<Building2 className="w-4 h-4" />} actions={<WidgetExportBtn title="Google Business Profile" data={[]} />}>
                  <EmptyState title="No local data yet" description="Import your authority audit to see your Google Business Profile completeness and optimisation." />
                </DataPanel>
                <DataPanel title="Citations & NAP Consistency" icon={<BookMarked className="w-4 h-4" />} actions={<WidgetExportBtn title="Citations" data={[]} />}>
                  <EmptyState title="No citation data yet" description="Import your authority audit to see your directory citations and NAP consistency." />
                </DataPanel>
                <DataPanel title="Local SEO Summary" icon={<MapPin className="w-4 h-4" />} actions={<WidgetExportBtn title="Local SEO Summary" data={[]} />}>
                  <EmptyState title="No local data yet" description="Import your authority audit to see your local SEO summary metrics." />
                </DataPanel>
              </>
            )}
          </div>

          {/* Right column — Ask Ruby Chat */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="sticky top-6 flex-1" style={{ height: "calc(100vh - 48px)" }}>
              <RubyQuickFixesWidget cardId={CARD_ID} activeTab={activeTab} onIssueFixed={() => loadDashboardAudit(activeSite?.domain)} />
            </div>
          </div>
        </div>
      </div>

      <RubyExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog((p) => ({ ...p, open: false }))}
        title={exportDialog.title}
        tier={exportDialog.tier}
        data={exportDialog.data}
        fileName={`OG01_Local_Intelligence_${lastAuditMeta?.domain || "report"}`}
        auditResult={exportDialog.auditResult}
        domain={lastAuditMeta?.domain}
      />
    </div>
  );
};

export default LocalIntelligenceDashboard;

